





















































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { AccountProfileComponents } from '@/enums/AccountProfileComponents';
import { BillingAddress } from '@/interfaces/BillingAddress';
import { Action } from 'vuex-class';
import { ElForm } from 'element-ui/types/form';
import { Country } from '@/interfaces/Country';
import {
    BackendValidationError,
    BillingAddressFormBackendErrorKeys,
} from '@/interfaces/BackendValidationError';
import {UserData} from "@/interfaces/UserData";

interface RefsInterface {
    billingAddressForm: ElForm;
}

@Component({
    name: 'AccountProfileBillingAddress',
})
export default class AccountProfileBillingAddress extends Vue {
    public $refs!: Vue['$refs'] & RefsInterface;

    @Prop({required: true}) public billingAddresses!: BillingAddress[];
    @Prop({required: true}) public userData!: UserData;

    @Action('user/updateBillingAddress')
    protected updateBillingAddress!: (
        billingAddressData: object,
    ) => Promise<unknown>;
    @Action('user/addBillingAddress')
    protected addBillingAddress!: (
        billingAddressData: object,
    ) => Promise<unknown>;
    @Action('user/getBillingAddresses')
    protected getBillingAddresses!: () => void;
    @Action('user/getCountryList') protected getCountryList!: () => {};

    @Prop() protected editableBillingAddress!: BillingAddress | null;

    protected get countryList() {
        return this.$store.getters['user/countryList'];
    }

    protected dialogVisible = false;
    protected fromPersonalProfile = false;

    protected billingAddressForm = {
        firstName: '',
        lastName: '',
        company: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        vatId: ''
    };

    protected billingAddressFormBackendErrors: BillingAddressFormBackendErrorKeys = {
        firstName: '',
        lastName: '',
        address1: '',
        city: '',
        zipCode: '',
        country: '',
    };

    protected formRules = {
        firstName: [
            {
                required: true,
                message: 'Please input the first name',
                trigger: 'change',
            },
        ],
        lastName: [
            {
                required: true,
                message: 'Please input the last name',
                trigger: 'change',
            },
        ],
        address1: [
            {
                required: true,
                message: 'Please input the street address',
                trigger: 'change',
            },
        ],
        city: [
            {
                required: true,
                message: 'Please input the city',
                trigger: 'change',
            },
        ],
        zipCode: [
            {
                required: true,
                message: 'Please input the postal code',
                trigger: 'change',
            },
        ],
        country: [
            {
                required: true,
                message: 'Please select the country',
                trigger: 'change',
            },
        ],
    };

    protected toggleComponent(
        componentName: keyof typeof AccountProfileComponents,
    ) {
        this.$emit('toggle-component', AccountProfileComponents[componentName]);
    }

    protected closeDialog() {
        this.dialogVisible = false;
    }

    protected openDialog() {
        this.dialogVisible = true;
    }

    protected resetBackendValidationErrors() {
        this.billingAddressFormBackendErrors = {
            firstName: '',
            lastName: '',
            address1: '',
            city: '',
            zipCode: '',
            country: '',
        };
    }

    protected parseBackendValidationErrors(errors: BackendValidationError[]) {
        const pointerPath = '/data/attributes/';
        errors.forEach((error) => {
            const fieldKey = error.source.pointer.replace(pointerPath, '');
            this.billingAddressFormBackendErrors[fieldKey] = error.detail;
        });
    }

    protected async saveBillingAddress() {
        this.resetBackendValidationErrors();
        this.closeDialog();
        try {
            if (this.editableBillingAddress) {
                await this.onUpdateBillingAddress();
            } else {
                await this.createBillingAddress();
            }
        } catch (e) {
            const errors = e.response.data.errors;
            this.parseBackendValidationErrors(errors);

            return;
        }

        await this.getBillingAddresses();
        this.toggleComponent('Addresses');
    }

    protected async createBillingAddress() {
        await this.addBillingAddress({
            attributes: { ...this.billingAddressForm },
        });
    }

    protected async onUpdateBillingAddress() {
        if (!this.editableBillingAddress) {
            return;
        }
        await this.updateBillingAddress({
            attributes: { ...this.billingAddressForm },
            id: this.editableBillingAddress.id,
        });
    }

    protected cancelBillingAddress() {
        this.closeDialog();
        this.toggleComponent('Addresses');
    }

    protected submitForm(formName: keyof RefsInterface) {
        this.$refs[formName].validate((valid) => {
            if (valid) {
                const countryEntry: Country | undefined = this.countryList.find(
                    (country: Country) =>
                        country.id === this.billingAddressForm.country,
                );
                if (
                    countryEntry &&
                    countryEntry.isEU &&
                    !this.billingAddressForm.company
                ) {
                    this.openDialog();
                    return;
                }
                this.saveBillingAddress();
            } else {
                return false;
            }
        });
    }

    protected resetForm(formName: keyof RefsInterface) {
        this.$refs[formName].resetFields();
    }

    protected validateFormField(formName: keyof RefsInterface, field: string) {
        this.$refs[formName].validateField(field, () => null);
    }

    protected populateFromPersonalProfile(isToggled: boolean) {
        if (isToggled) {
            this.billingAddressForm.firstName = this.userData.firstName ? this.userData.firstName : '';
            this.billingAddressForm.lastName = this.userData.lastName ? this.userData.lastName : '';
            this.billingAddressForm.company = this.userData.company ? this.userData.company : '';
            this.billingAddressForm.address1 = this.userData.streetAddress ? this.userData.streetAddress : '';
            this.billingAddressForm.city = this.userData.city ? this.userData.city : '';
            this.billingAddressForm.country = (this.userData.country && this.userData.country.id ? this.userData.country.id : '');
        } else {
            if (this.billingAddressForm.firstName === this.userData.firstName)
                this.billingAddressForm.firstName = '';
            if (this.billingAddressForm.lastName === this.userData.lastName)
                this.billingAddressForm.lastName = '';
            if (this.billingAddressForm.company === this.userData.company)
                this.billingAddressForm.company = '';
            if (this.billingAddressForm.address1 === this.userData.streetAddress)
                this.billingAddressForm.address1 = '';
            if (this.billingAddressForm.city === this.userData.city)
                this.billingAddressForm.city = '';
            if (this.billingAddressForm.country === (this.userData.country && this.userData.country.id ? this.userData.country.id : ''))
                this.billingAddressForm.country = '';
        }
    }

    protected mounted() {
        this.getCountryList();
        if (this.editableBillingAddress) {
            this.billingAddressForm.firstName = this.editableBillingAddress
                .firstName
                ? this.editableBillingAddress.firstName
                : '';
            this.billingAddressForm.lastName = this.editableBillingAddress
                .lastName
                ? this.editableBillingAddress.lastName
                : '';
            this.billingAddressForm.company = this.editableBillingAddress
                .company
                ? this.editableBillingAddress.company
                : '';
            this.billingAddressForm.address1 = this.editableBillingAddress
                .address1
                ? this.editableBillingAddress.address1
                : '';
            this.billingAddressForm.address2 = this.editableBillingAddress
                .address2
                ? this.editableBillingAddress.address2
                : '';
            this.billingAddressForm.city = this.editableBillingAddress.city
                ? this.editableBillingAddress.city
                : '';
            this.billingAddressForm.state = this.editableBillingAddress.state
                ? this.editableBillingAddress.state
                : '';
            this.billingAddressForm.zipCode = this.editableBillingAddress
                .zipCode
                ? this.editableBillingAddress.zipCode
                : '';
            this.billingAddressForm.country = this.editableBillingAddress
                .country
                ? this.editableBillingAddress.country.id
                : '';
            this.billingAddressForm.vatId = this.editableBillingAddress.vatId
                ? this.editableBillingAddress.vatId
                : '';
        }
    }
}
